<template>
  <ion-page id="explorer-page">

    <AlertReplacement :alertInfo="alertInfo" :trigger="alertTrigger"
              @alert-result="alertResult"></AlertReplacement>

    <ion-content>

      <div v-show="showTour">
        <Tour  :tour="tourInfo" :tourTrigger="tourTrigger" @tour-done="tourDone" />
      </div>

      <div id="the_board" :style="styleBoard">
        <CMChessboard :fen="fen" :validMoves="availableMoves"
                    :orientation="orientation" 
                    :boardSize="boardSize" @board-move="boardMove" />
      </div>

      <div id="the_slice_movelist"
                  :class="{ 'text-dark' : isDark,'text-light' : !isDark }" :style="styleMoveList">
        <MoveListSelector :resultsData="availableMoves" :eighty="eighty"
              :moveNumber="moveNumberValue"
              :showCheckBoxes=true
              :showPercentage="!showUserGames"
              :moveListTrigger="availableMovesTrigger" 
              @checkbox-click="moveListCheckboxClick" 
              @move-click="moveListMoveClick" />
      </div>

      <div id="the_slice_chart"
              :class="{ 'text-dark' : isDark,'text-light' : !isDark }" 
              :style="styleChart">
        <div>
          <SliceDiceAgChart :moveList="availableMoves" :moveNumber="moveNumberValue" 
              :showAdvantage="showWhiteAdvantage" :moveListTrigger="availableMovesTrigger" 
              :styleHeight="styleChartAgHeight"/> 

          <!-- move number for bar chart -->
          <div id="the_slider"  :style="styleMoveNumberSlider">
            <ion-list>
              <ion-item> 
                <!-- move number for bar chart -->
                <ion-button size="small" @click="toggleWhiteAdvantage()">
                    {{ whiteAdvantageLabel }} @ {{ t("Move") }} {{ moveNumberValue }} 
                </ion-button>
                <ion-range aria-label="Range with ticks" :ticks="true"    
                          :snaps="true" v-model="moveNumberValue"
                          :min="12" :max="21" :step="3" class="move_slider_style"
                          @ionChange="moveNumberChange">
                </ion-range>
              </ion-item>
            </ion-list>
          </div>
        </div>
      </div>

      <div id="the_movehistory" 
                :style="styleMoveHistory" 
                :class="{ 'text-dark' : isDark, 'text-light' : !isDark }">
        <span v-if="moveHistoryStack.length == 0">
          <span v-if="showUserGames">
            <b>{{ t("Analyzing Your Downloaded Games") }}</b>
          </span>
          <span v-if="!showUserGames">
            <b>{{ t("Explorer") }}: {{ t("Rating") }}: {{ barMinValue }} {{ t("to") }} {{ barMaxValue }}</b>
          </span>
        </span>
        <MoveHistory :moveHistoryStack="moveHistoryStack" :trigger="moveHistoryTrigger" 
                          @move-history-string="moveHistoryStringUpdate"/>
      </div>
    </ion-content>
    <Toolbar :elements="toolbarElements" :trigger="toolbarTrigger" 
                        :ignoreClicks="showTour"         
                          @toolbar-click="toolbarClick"/>
  </ion-page>
</template>

<script setup>

  import { IonItem, IonList, IonButton, IonRange,IonPage, IonContent, modalController } from '@ionic/vue';

  import { t } from "@/composables/translateText.js";

  import Toolbar from '@/components/Toolbar.vue';
  import AlertReplacement from '@/components/AlertReplacement.vue'

  import MoveListSelector from '@/components/MoveListSelector.vue'
  import SliceDiceAgChart from '@/components/SliceDiceAgChart.vue'
  import Tour from '@/components/Tour.vue'

  import CMChessboard from '@/components/CMChessboard.vue'
  import MoveHistory from '@/components/MoveHistory.vue'

  import ModalExplorerRange from '@/components/ModalExplorerRange.vue'
  import ModalErrorMessage from '@/components/ModalErrorMessage.vue'

  import { ref, watch } from 'vue'
  import { findToolbarElement, setToolbarElementDisabledFlag, setToolbarElementLabel} 
                from '@/composables/toolbarSupport.js'
  import { useGetData , getLichessData, getChesscomData, 
              generateErrorAlertText } from '@/composables/getData.js'
  import { useConnectionStore } from '@/stores/connectionStore';
  import { onMounted, onBeforeUnmount } from 'vue'

  import { doStartupCheck } from '@/composables/startupCheck.js'
  import { availableWidthHeight, calculateChessBoardRect, isScreenPortraitOrientation } 
              from '@/composables/screenSize'
  import { useWindowSizeStore } from '@/stores/windowSizeStore';

  const TOOLBAR_BACK = 0
  const TOOLBAR_REWIND = 1
  const TOOLBAR_FLIP = 2
  const TOOLBAR_RATING = 3
  const TOOLBAR_COPY = 4
 
  const INITIAL_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"

  const MOVEHISTORY_FONTSIZE = 16
  const MOVEHISTORY_HEIGHT =  MOVEHISTORY_FONTSIZE * 1.2 * 2 + 4

  const emit = defineEmits(['boardMove'])

  const connectionStore = useConnectionStore()
  var explorerType = connectionStore.getProductOption

  // TESTING
  //explorerType = "user"

  const isDark = ref(connectionStore.getDarkmode != "light")
  //const isDark = window.matchMedia('(prefers-color-scheme: dark)');

  const menuTrigger = ref(0)

  const showUserGames = ref(explorerType == "user")
  const menuName = ref((explorerType == "user") ? "Your Games" : "Explorer")
  var isSubscriber = (connectionStore.getIsSubscriber == 1)

  const tourTrigger = ref(0)
  const tourOption = connectionStore.getTourOption
  //console.log("*** Tour option",tourOption)
  const showTour = ref((tourOption == "show"))

  const alertInfo = ref({})
  const alertTrigger = ref(0)

  const toolbarExplorerElements = [
    { index : TOOLBAR_BACK, label : t("Back") , disabled : true, collapse:false},
    { index : TOOLBAR_REWIND, label : t("Rewind") , disabled : true, collapse:false},
    { index : TOOLBAR_FLIP, label : t("Flip") , disabled : false, collapse:false},
    { index : TOOLBAR_RATING, label : t("Rating") , disabled : false },
    { index : TOOLBAR_COPY, label : t("Copy") , disabled : false },
    
  ]

  const toolbarUserGamesElements = [
    { index : TOOLBAR_BACK, label : t("Back") , disabled : true, collapse:false},
    { index : TOOLBAR_REWIND, label : t("Rewind") , disabled : true, collapse:false},
    { index : TOOLBAR_FLIP, label : t("Flip") , disabled : false, collapse:false}
  ]

  const toolbarElements = ref((showUserGames.value) 
                  ? toolbarUserGamesElements :toolbarExplorerElements)

  const barMin = ref(800);
  const barMax = ref(2550);
  const barStep = ref(50)

  const barMinValue = ref(1000)
  const barMaxValue = ref(2000)

  const whiteAdvantageLabel = ref(t("All") + " ")
  const windowSizeStore = useWindowSizeStore()
  const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] })
  const boardSize = ref(100)

  
  const fen = ref(INITIAL_FEN)
  const initialFenid4 = "147869767"
  const initialFenid8 = "1"
  const eighty = ref(-2)
  const showScores = ref(true)

  var playingWhite = 1
  var blankAppendTrigger = " "
  var orientation = ref("white")

  var maxSpreadsheetColumnNumber = 0
  var spreadsheetColumnDefinitions = ref([])

  // what comes back from the server
  var mainMoves = []

  // merged result from the server grabs
  const availableMoves = ref([])

  const moveHistoryStack = ref([])
  const moveHistoryTrigger = ref(0)
  var moveHistoryString = ""
  const toolbarTrigger = ref(0)
  const gameCollectionTrigger = ref(0)

  const moveNumberValue = ref(15)
  const availableMovesTrigger = ref(0)
  const showWhiteAdvantage = ref(false)
  const styleChartAgHeight = ref("height: 500px")
  var saveSelectedSans = []
  var selectedSansStack = []

  const tourInfo = ref({
    id : "opening_slicedice",
    elements : [
      { id: "", title: "Explore", text : "Why are we deciding our opening moves based on how the game ends 40 to 60 moves later?<br><br>That's an unfair burden for the game's first few moves."},
      { id: "", title: "There Is A Better Way!", text : "Chess Opening Mastery is a game changing, data driven approach to evaluating the game's first moves.<br><br>"},
      { id: "", title: "A Window into the Middle Game", text : "Chess Opening Mastery's revolutionary approach shows how the games are progressing in the middle game.<br><br>How? For every game, the Stockfish computer engine has been used to evaluate the position before white's moves 12, 15, 18 and 21. Stockfish scores each board as having an advantage for white (positive numbers) or an advantage for black (negative numbers)." },
      { id: "", title: "Stockfish Scores", text : "You probably know that a pawn is worth 1 point, a bishop/knight is worth 3 and a rook is 5 points. Stockfish reports back a position as being the equivalent of 'up' some number of pawns.<br><br>The bar chart shows that distribution for games start with the given move sequence."},
      { id: "", title: "The Game Collection", text : "The Lichess 'rapid' games were used to build the tree. With a unique twist - only games where the players are within the same 50 point 'bucket' are included.<br><br>Why? Because even a 50 point difference between two players gives the higher rated player a 6% win advantage which swamps any advantage that can be obtained by a particular opening."},
      { id: "the_slice_movelist", title: "The Move List", text : "The move choices are sorted in order of popularity."},
      { id: "the_slice_movelist", title: "The Percentage", text : "This is the percentage of your games where you'll see each move. Obviously, when it is your move you can choose any move and therefore it's popularity is not important. (This was a real breakthrough in thinking and why white at move 1 sees 100% for every choice.)"},
      { id: "the_slice_movelist", title: "Opponent Move Percentage", text : "A large part of opening exploration, is making sure you cover your opponent's more popular moves and not waste time thinking about rarely played moves."},
      { id: "the_slice_movelist", title: "Weighted score", text : "Next to the percentage is a weighted average of the Stockfish scores at the 'future move' specified on the slider. This gives you a quick idea of the future state of the game for each current move."},
      { id: "the_slice_movelist", title: "Game Count", text : "The game count is shown so you can evaluate the validity of the future move results. I.e., extraordinary results for rarely played moves should be viewed with caution."},
      { id: "the_slice_movelist", title: "Selecting Moves", text : "Clicking the move or checking the box, will add the move to the chart."},
      { id: "the_slider", title: "Selecting the Move Number", text : "The important slider is the <b>Move Number</b>. That is the <i>future</i> move that will be used for the bar chart.<br><br>The available future moves are: 12, 15, 18, and 21."},
      { id: "the_slider", title: "White's Advantage", text : "For whatever color you're playing, you want moves where your bars are 'higher'. When you find yourself looking back and forth between the equivalent white and black bars, this button will show you the difference between the corresponding bars.<br><br>For the black player then shorter bars are better"},
      { id: "the_slice_chart", title: "The Graph", text : "The graph shows the distribution of the Stockfish engine scores for games where the checked move(s) was made.<br><br>For example, if '1.e4' is checked and the slider is at move 15 then the graph shows how well players who played 'e4' are doing in their games at move 15."},
      { id: "the_slice_chart", title: "The Bars", text : "The bars show the percentage of games in each bucket. The buckets start on the left with black having already won the game by the future move number. Next to that is the percentage of games where black has a 'huge' advantage"},
      { id: "the_slice_chart", title: "The Point Bars", text : "The next several bars break the stockfish engine scores into one point buckets.<br><br><i>(The 3 point bucket represents being up a bishop and the 1 point bucket represents being up by a pawn.)</i>"},
      { id: "the_slice_chart", title: "The Middle &amp; White's Bars", text : "The center bar shows the percentage of games that are about even.<br><br>The bars to the right are white's mirroring the black buckets from 1 point through already won games."},
      { id: "the_toolbar", title: "Options", text : "Used to select color and rating range. Be sure to watch the videos."},
    ]
  })

  const styleChart = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleMoveList = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleBoard = ref({
    position : "absolute",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleMoveHistory = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px",
    "font-size" : MOVEHISTORY_FONTSIZE + "px",
    "text-align" : "left",
    "line-height" : "1.2em"
  })

  const styleMoveNumberSlider = ref({
  })

  doStartupCheck(false,afterStartupCallbackRoutine)

  const openErrorModal = async (programName,errorMessage) => {
    const modal = await modalController.create({
      component: ModalErrorMessage,
      componentProps: {
        type : "commError",
        program : programName ,
        errorMessage : errorMessage
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();
  };

  function afterStartupCallbackRoutine(stuffChanged,errorProgram,errorEntry) {
    if (errorProgram != null)
      openErrorModal(errorProgram,errorEntry)
    isSubscriber = (connectionStore.getIsSubscriber == 1)

    updateWidthHeights()
    getInitialUserInfo()
  }

  onMounted(() => {
  })

  const openRatingModal = async () => {
    const modal = await modalController.create({
      component: ModalExplorerRange,
      componentProps: {
        min : barMinValue.value,
        max : barMaxValue.value
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      var didChange = false
      if (data.lower != barMinValue.value) {
        didChange = true
        barMinValue.value = data.lower
      }
      if (data.upper != barMaxValue.value) {
        didChange = true
        barMaxValue.value = data.upper
      }

      //console.log("CONFIRM",data,didChange)

      if (didChange)
        updateUserInfo("rating")
    }
  };

  function resetExplorerScreen() {
    // reset to beginning
    showUserGames.value = (isSubscriber && explorerType == "user")
    const menuName = ref((explorerType == "user") ? "Your Games" : "Explorer")
    toolbarElements.value = (showUserGames.value) 
                  ? toolbarUserGamesElements :toolbarExplorerElements

    afterStartupCallbackRoutine(true)
  }

  watch(() => connectionStore.getProductOption, () => {
    explorerType = connectionStore.getProductOption
    resetExplorerScreen()
  })

  watch(() => windowSizeStore.getWindowSize, () => {
    windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                      height : windowSizeStore.getWindowSize['height'] }
    updateWidthHeights()
    menuTrigger.value++
  })

  function updateStyleObjects(boundaries) {
    boardSize.value = boundaries['board'].height

    styleBoard.value.height = boundaries['board'].height + "px"
    styleBoard.value.width = boundaries['board'].width + "px"
    styleBoard.value.top = boundaries['board'].top + "px"
    styleBoard.value.left = boundaries['board'].left + "px"

    styleMoveList.value.height = boundaries['moveList'].height + "px"
    styleMoveList.value.width = boundaries['moveList'].width + "px"
    styleMoveList.value.top = boundaries['moveList'].top + "px"
    styleMoveList.value.left = boundaries['moveList'].left + "px"

    styleMoveHistory.value.height = boundaries['history'].height + "px"
    styleMoveHistory.value.width = boundaries['history'].width + "px"
    styleMoveHistory.value.top = boundaries['history'].top + "px"
    styleMoveHistory.value.left = boundaries['history'].left + "px"

    styleChart.value.height = boundaries['chart'].height + "px"
    styleChart.value.width = boundaries['chart'].width + "px"
    styleChart.value.top = boundaries['chart'].top + "px"
    styleChart.value.left = boundaries['chart'].left + "px"

    const buttonHeight = 47
    styleChartAgHeight.value = "height: " + (boundaries['chart'].height - buttonHeight) + "px"
 
    styleMoveNumberSlider.value.width =  "350px"
    styleMoveNumberSlider.value['margin-left'] =  (boundaries['chart'].width/2 - 175) + "px"
  }

  function updateWidthHeights() {
    const availableSpace = availableWidthHeight()
    const isPortrait = isScreenPortraitOrientation(availableSpace)
    const boundaries = (isPortrait) ? calculatePortraitWidthHeights(availableSpace) :
                                      calculateLandscapeWidthHeights(availableSpace)
    updateStyleObjects(boundaries)
  }

  function calculatePortraitWidthHeights(availableSpace) {

    // Vertically -> Board, move history, (move list & chart side by side), sliders
    const availableSpaceForBoard = { width : availableSpace.width ,
                height : availableSpace.height / 2.5,
                isPortrait : true }

    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)
    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

    const historyPositionTop = boardPositionTop + boardHeight
    const historyPositionLeft = availableSpace.leftRightIndent
    const historyHeight = MOVEHISTORY_HEIGHT
    const historyWidth = availableSpace.width - availableSpace.leftRightIndent * 2

    const moveListPositionTop = historyPositionTop  + historyHeight
    const moveListPositionLeft = availableSpace.leftRightIndent
    const moveListHeight = availableSpace.height - moveListPositionTop - MOVEHISTORY_FONTSIZE * 1.2
    const moveListWidth = 250

    const chartPositionTop = moveListPositionTop
    const chartPositionLeft = moveListPositionLeft + moveListWidth
    const chartHeight = moveListHeight
    const chartWidth = availableSpace.width - moveListWidth

    const result = { board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop+ availableSpace.topIndent
      },
      moveList : {
        width : moveListWidth,
        height : moveListHeight,
        left : moveListPositionLeft + availableSpace.leftIndent,
        top : moveListPositionTop+ availableSpace.topIndent
      },
      history : {
        width : historyWidth,
        height : historyHeight,
        left : historyPositionLeft + availableSpace.leftIndent,
        top : historyPositionTop+ availableSpace.topIndent
      },
      chart : {
        width : chartWidth,
        height : chartHeight,
        left : chartPositionLeft + availableSpace.leftIndent,
        top : chartPositionTop+ availableSpace.topIndent
      }
    }
    return (result)
  }

  function calculateLandscapeWidthHeights(availableSpace) {

    // reserve space for the instruction lines
    const availableSpaceForBoard = { width : availableSpace.width/4,
                height : availableSpace.height * .75,
                isPortrait : false }
    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

    const moveListPositionTop = 0
    const moveListPositionLeft = boardPositionLeft + boardWidth + availableSpace.leftRightIndent
    const moveListHeight = availableSpace.height
    const moveListWidth = 250

    const chartPositionTop = 0
    const chartPositionLeft = moveListPositionLeft + moveListWidth
    const chartHeight = availableSpace.height
    const chartWidth = availableSpace.width - chartPositionLeft

    const historyPositionTop = boardPositionTop + boardHeight + 5
    const historyPositionLeft = availableSpace.leftRightIndent
    const historyHeight = availableSpace.height - historyPositionTop
    const historyWidth = boardWidth

    const result = { board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop+ availableSpace.topIndent
      },
      moveList : {
        width : moveListWidth,
        height : moveListHeight,
        left : moveListPositionLeft + availableSpace.leftIndent,
        top : moveListPositionTop+ availableSpace.topIndent
      },
      history : {
        width : historyWidth,
        height : historyHeight,
        left : historyPositionLeft + availableSpace.leftIndent,
        top : historyPositionTop+ availableSpace.topIndent
      },
      chart : {
        width : chartWidth,
        height : chartHeight,
        left : chartPositionLeft + availableSpace.leftIndent,
        top : chartPositionTop+ availableSpace.topIndent
      }
    }
    return (result)
  }

  // For Slice & Dice
  function moveListCheckboxClick(item) {
    //console.log("move list checkbox",item)
    availableMoves.value[item.index].selected = item.flag
    availableMovesTrigger.value++
  }

  function moveListMoveClick(index) {
    boardMove(index)
  }

  // End for Slice & Dice

  function incrementAppendTrigger() {
    if (blankAppendTrigger.length > 64)
      blankAppendTrigger = ""
    blankAppendTrigger += " "
  }

  function updateValues(e) {
    barMinValue.value = e.minValue;
    barMaxValue.value = e.maxValue;   
    if (barMaxValue.value == barMinValue.value) {
      if (barMinValue.value < barMax.value)
        barMaxValue.value = barMinValue.value + 50
      else
        barMinValue.value = barMaxValue.value - 50
    }
  }

  function setSliderValues() {
    if (barMaxValue.value > barMax.value)
      barMaxValue.value = barMax.value
    if (barMinValue.value < barMin.value)
      barMinValue.value = barMin.value
  }

  function moveNumberChange({ detail }) {
    //moveNumberValue.value = detail.value
    //console.log("move number change",detail,detail.value,moveNumberValue.value)
    availableMovesTrigger.value++
  }

  function toggleWhiteAdvantage() {
    showWhiteAdvantage.value = !showWhiteAdvantage.value
    whiteAdvantageLabel.value = (showWhiteAdvantage.value) ? t("White") + "+" : t("All") 
    availableMovesTrigger.value++
  }

  function tourDone() {
    showTour.value = false
  }

  function formatDate(date,separator) {
    const year = date.toLocaleString('default', {year: 'numeric'});
    const month = date.toLocaleString('default', {
      month: '2-digit',
    });
    const day = date.toLocaleString('default', {day: '2-digit'});

    return [year, month, day].join(separator);
  }

  function moveHistoryStringUpdate(moveString) {
    //console.log("Move history String ",moveString)
    moveHistoryString = moveString
  }

  function buildPGN() {
      
    const todayDate = formatDate(new Date(),".")
    var bigString = "[Event \"Chessvis.com\"]\n"
    bigString += "[Site \"Explorer PGN\"]\n"
    bigString += "[Date \"" + todayDate + "\"]\n"
    bigString += "[Round \"1\"]\n"
    bigString += "[White \"White\"]\n"
    bigString += "[Black \"Black\"]\n"
    bigString += "[Result \"*\"]\n\n"
    bigString += moveHistoryString
    bigString += " *\n\n"

    //console.log("PGN",bigString)
    navigator.clipboard.writeText(bigString);
  }


  function alertResult(response,task,info) {
    //console.log("Alert Result",response,task,fen.value)

    if (response == "no")
      return

    if (response == "fen") {
      const copyFen = fen.value.trim()
      var fenPieces = copyFen.split(" ")
      fenPieces[5] = 1
      const fenFixed = fenPieces.join(" ")
      //console.log("fenfixed",fenFixed)
      navigator.clipboard.writeText(fenFixed);
    }
    else {
      buildPGN()
    }
  }

  function toolbarClick(index) {
    const element = findToolbarElement(toolbarElements.value,index)
    if (element.disabled)
      return

    switch (index) {
      case TOOLBAR_COPY :
        alertInfo.value = { header : t("Copy to Clipboard?") , 
                message : t("'FEN' of current board OR entire 'PGN'?"), type : "fenpgn", task : "fenpgn"  }
        alertTrigger.value++
        break
      case TOOLBAR_RATING :
        openRatingModal()
        break
      case TOOLBAR_FLIP :
        incrementAppendTrigger()
        playingWhite = 1 - playingWhite
        orientation.value = (playingWhite == 1) ? "white" + blankAppendTrigger : "black" + blankAppendTrigger
        updateUserInfo("color")
        break
      case TOOLBAR_REWIND :
        fen.value = INITIAL_FEN + " "

        saveSelectedSans = (selectedSansStack.length) ? selectedSansStack[0] : []
        selectedSansStack = []

        selectedSansStack.pop()

        moveHistoryStack.value = []
        moveHistoryTrigger.value++
        getAvailableMoves(initialFenid4,initialFenid8,100,false)
        break
      case TOOLBAR_BACK :
        if (moveHistoryStack.value.length <= 1) {
          toolbarClick(TOOLBAR_REWIND)
        }
        else {
          saveSelectedSans = selectedSansStack[selectedSansStack.length - 1]
          selectedSansStack.pop()

          moveHistoryStack.value.pop()
          moveHistoryTrigger.value++

          fen.value = moveHistoryStack.value[moveHistoryStack.value.length - 1].fen

          getAvailableMoves(moveHistoryStack.value[moveHistoryStack.value.length - 1].fenid4,
                moveHistoryStack.value[moveHistoryStack.value.length - 1].fenid8,
                moveHistoryStack.value[moveHistoryStack.value.length - 1].movePercentage,false)
        }
        break    
    }
  }

  function boardMove(arrayIndex) {
    //console.log("board move ",arguments)

    // save current move's sans before getting next ply
    saveSelectedSans = []
    for (var i=0; i < availableMoves.value.length; i++)
      if (availableMoves.value[i].selected)
        saveSelectedSans.push(availableMoves.value[i].san)
    selectedSansStack.push(saveSelectedSans)
    saveSelectedSans = []

    fen.value = availableMoves.value[arrayIndex].fen
    
    const fenid4 = availableMoves.value[arrayIndex].fenid4
    const fenid8 = availableMoves.value[arrayIndex].fenid8
    const san = availableMoves.value[arrayIndex].san
    const boardFromSquare = availableMoves.value[arrayIndex].from
    const boardToSquare = availableMoves.value[arrayIndex].to
    const previousMovePercentage = availableMoves.value[arrayIndex].movePercentage

    const oneMove = {fen : fen.value, fenid4 : fenid4, fenid8 : fenid8,
                          boardToSquare : boardToSquare, 
                          boardFromSquare : boardFromSquare,
                          san : san, movePercentage : previousMovePercentage,
                          openingName : "" }
    moveHistoryStack.value.push(oneMove)
    moveHistoryTrigger.value++

    getAvailableMoves(fenid4,fenid8,previousMovePercentage,false)
  }

  function processAvailableMovesJsonResponse (json) {
    //console.log("*** in process json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    const whiteString = json['white']
    const blackString = json['black']
    const whites = whiteString.split("\t")
    const blacks = blackString.split("\t")
    const sans = json['san'].split("\t")
    const move_percentages = (showUserGames.value) ? [] : json['percentage'].split("\t")
    const pretty_game_counts = json['pretty'].split("\t")

    //if was a recalc then update the movehistory stack
    const recalc_percentages_string = (showUserGames.value) ? "" : String(json['recalc_percentages'])
    if (recalc_percentages_string.length > 0) {
      const recalc_percentages = recalc_percentages_string.split("\t")
      for (var i = 0; i < recalc_percentages.length; i++)
        moveHistoryStack.value[i].movePercentage = recalc_percentages[i]
    }

    const fens = json['fen'].split("\t")
    const fenid4s = json['fenid4'].split("\t")
    const fenid8s = (showUserGames.value) ? json['fenid8'].split("\t") : []
    const froms = json['from'].split("\t")
    const tos = json['to'].split("\t")

    var inRange = (showUserGames.value) ? [] : json['in_range'].split("\t")
    if (showUserGames.value) {
      for (var i=0; i < whites.length;i++)
        inRange.push("1")
    }

    const count = json['count']

    var scores = []
    const weights = [ -6, -6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 6]

    eighty.value = 100
    
    scores = json['scores'].split("\t")

    availableMoves.value = []

    for (var i=0; i < count; i++) {
      const whiteNumber = Number(whites[i])
      const blackNumber = Number(blacks[i])
      const totalNumber = whiteNumber + blackNumber

      const whitePercent = (totalNumber > 0) ? whiteNumber * 100 / totalNumber : 0
      const blackPercent = (totalNumber > 0) ? blackNumber * 100 / totalNumber : 0
      const whitePercentString = whitePercent.toFixed(1)
      const blackPercentString = blackPercent.toFixed(1)

      // build the bar chart info
      var moves = []
      var weightedScores = []
      var isSelected = false

      const moveScores = scores[i].split(",")
      for (var moveIndex = 0; moveIndex < 5; moveIndex++) {
        const offset = moveIndex * 15
        var weight = 0
        var percentages = []
        for (var percentIndex = 0; percentIndex < 15; percentIndex++) {
          const oneNumber = Number(moveScores[offset + percentIndex])
          weight += oneNumber * weights[percentIndex]
          percentages.push(oneNumber)
        }
        moves.push(percentages)
        weightedScores.push((weight/100).toFixed(2))
      }

      // if there is a saved selected list, use it, otherwise use the first two
      if (saveSelectedSans != null && saveSelectedSans.length) {
        var isSelected = false
        for (var j=0; j < saveSelectedSans.length; j++)
          if (sans[i] == saveSelectedSans[j]) {
            isSelected = true
            break
          }
      }
      else
        isSelected = (i < 2)

      const percentage = Number(move_percentages[i])
      const displayPercentage = (inRange[i] != "1") ? "all" : percentage.toFixed(2) + "%"

      const oneMove = { index: i, 
                        fenid4: fenid4s[i],
                        fenid8 : (showUserGames.value) ? fenid8s[i] : 0,
                        white : whitePercentString, black : blackPercentString, 

                        // Slice & Dice
                        weightedScores : weightedScores,
                        selected : isSelected,
                        moves : moves,

                        from : froms[i], to: tos[i],
                        countDisplay : pretty_game_counts[i],
                        textType : "player",
                        san : sans[i], 
                        percentDisplay : displayPercentage,
                        movePercentage : (showUserGames.value) ? 0 : move_percentages[i],
                        fen : fens[i],
                        openingName : "" 
                      }
          
      availableMoves.value.push(oneMove)
    }

    availableMovesTrigger.value++

    // update the toolbar
    const ply = moveHistoryStack.value.length
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_BACK,(ply >= 1) ? false : true)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REWIND,(ply >= 1) ? false : true)
    toolbarTrigger.value++
  }

  function debounce(callback, interval=500) {
    let debounceTimeoutId;

    return function(...args) {
      clearTimeout(debounceTimeoutId);
      debounceTimeoutId = setTimeout(() => callback.apply(this, args), interval);
    };
  }

  function getAvailableMoves(fenid4,fenid8,previousMovePercentage,doRecalc) {
    if (showUserGames.value)
      getUserMoves(fenid4,fenid8,100,false)
    else
      getAvailableDatabaseMoves(fenid4,fenid8,previousMovePercentage,doRecalc)
  }

  function getAvailableDatabaseMoves(fenid4,fenid8,previousMovePercentage,doRecalc) {
    // get the previous move stack if recalc
    var recalcString = ""

    if (doRecalc && moveHistoryStack.value.length > 0) {
      var recalcFenids = []
      for (var i = 0; i < moveHistoryStack.value.length; i++)
        recalcFenids.push(moveHistoryStack.value[i].fenid4)
      recalcString = recalcFenids.join(",")
    }

    var formData = {
      parent_unique : "0",
      fenid4 : fenid4,
      previous_percent : previousMovePercentage,
      white_to_move : 1 - (moveHistoryStack.value.length % 2),
      playing_white : playingWhite,
      min_rating : barMinValue.value,
      max_rating : barMaxValue.value - 1,
      recalc_fenid4s : recalcString
    }

    // zap waiting for next move values
    availableMoves.value = []
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_BACK,true)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REWIND,true)

    eighty.value = -2

    //console.log("before post for availablemoves -> ",formData)

    useGetData("opening_explorer_move4.php", formData, processAvailableMovesJsonResponse)
  }

  function getUserMoves(fenid4,fenid8) {
    var formData = {
      fenid4 : fenid4, 
      fenid8 : fenid8,
      player_white : playingWhite,
      white_to_move : 1 - (moveHistoryStack.value.length % 2),
      is_main : "main",
      future : "yes",
    }

    // zap waiting for next move values
    availableMoves.value = []

    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_BACK,false)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REWIND,false)
    toolbarTrigger.value++

    eighty.value = -2
    //console.log("before post to get USER moves-> ",formData)

    useGetData("getweb_user_slice_dice.php", formData, processAvailableMovesJsonResponse)
  }

  function processInitialJsonResponse (json) {
    //console.log(" in initial json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    barMinValue.value = Number(json['rating_min'])

    // on the server, the value is 49 & 99 but for display, let's increment it by 1
    barMaxValue.value = Number(json['rating_max']) + 1

    playingWhite = parseInt(json['playing_white'])
    if (playingWhite == 0)
      orientation.value = "black"

    setSliderValues(true)
    getAvailableMoves(initialFenid4,initialFenid8,100,false)
    menuTrigger.value++
  }

  function getInitialUserInfo() {
    var formData = { }
    useGetData("opening_explorer_info2.php", formData, processInitialJsonResponse)
  }

  function processUpdateUserJsonResponse(json) {
    //console.log(" in update user json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    if (moveHistoryStack.value.length == 0)
      getAvailableMoves(initialFenid4,initialFenid8,100,true)
    else
      getAvailableMoves(moveHistoryStack.value[moveHistoryStack.value.length - 1].fenid4,
                moveHistoryStack.value[moveHistoryStack.value.length - 1].fenid8,
                moveHistoryStack.value[moveHistoryStack.value.length - 1].movePercentage,true)
  }

  function updateUserInfo(updateAction) {
    var formData = { 
      action : updateAction,
      rating_max : barMaxValue.value -1,
      rating_min : barMinValue.value,
      playing_white : playingWhite
    }

    //console.log("formdata for update user info",formData)
    useGetData("opening_update_user_info.php", formData, processUpdateUserJsonResponse)
  }
  
</script>

<style scoped>

/* ion-content {
    padding-top: var(--ion-safe-area-top, 0);
    padding-bottom: var(--ion-safe-area-bottom, 0);
    padding-left: var(--ion-safe-area-left, 0);
    padding-right: var(--ion-safe-area-right, 0);
  } */

  ion-list {
    padding-top : 0px;
    padding-bottom : 0px;
  }
.move_slider_style {
  width : 50px;
}

</style>
